/**
 * Board layout utilities — grid-only model.
 * All positions are { x, y, w, h } in grid units.
 * Legacy row-model exports have been removed.
 */

// ─── Grid configuration ────────────────────────────────────────────────────

export const GRID_COLS = 12;
/** 40px rows give finer resize granularity than the old 80px rows. */
export const GRID_ROW_HEIGHT_PX = 40;
export const GRID_MARGIN = [10, 10];
export const GRID_CONTAINER_PADDING = [0, 0];

// ─── Per-type defaults and minimums ────────────────────────────────────────

/**
 * Default grid dimensions for each card type.
 * With rowHeight=40 and margin=10: h:6 ≈ 290px, h:7 ≈ 340px, h:8 ≈ 390px.
 */
export const CARD_DEFAULTS = {
  insights:     { w: 6,  h: 7,  minW: 3, minH: 3 },
  funnel:       { w: 6,  h: 8,  minW: 4, minH: 4 },
  retention:    { w: 6,  h: 7,  minW: 4, minH: 3 },
  flow:         { w: 8,  h: 9,  minW: 5, minH: 5 },
  custom_chart: { w: 6,  h: 7,  minW: 3, minH: 3 },
  text:         { w: 4,  h: 3,  minW: 2, minH: 1 },
  media:        { w: 4,  h: 6,  minW: 2, minH: 2 },
  quick_links:  { w: 4,  h: 4,  minW: 3, minH: 2 },
  team_widget:  { w: 6,  h: 6,  minW: 4, minH: 3 },
};

/** Kept for legacy compat during imports that reference old constant name. */
export const GRID_DEFAULT_SIZE_BY_TYPE = Object.fromEntries(
  Object.entries(CARD_DEFAULTS).map(([k, v]) => [k, { w: v.w, h: v.h }])
);

/**
 * MCP / agent layout presets.
 * Server resolves preset → { w, h }, then findFreeSlot fills in { x, y }.
 */
export const LAYOUT_PRESETS = {
  kpi:   { w: 3,  h: 4 },
  chart: { w: 6,  h: 7 },
  wide:  { w: 8,  h: 7 },
  full:  { w: 12, h: 8 },
  table: { w: 6,  h: 9 },
};

/** Header height inside each card shell (title row + divider). */
export const GRID_CARD_HEADER_PX = 44;

// ─── Helpers ───────────────────────────────────────────────────────────────

function getDefaults(cardType) {
  return CARD_DEFAULTS[(cardType || '').toLowerCase()] || { w: 6, h: 7, minW: 2, minH: 2 };
}

/**
 * Build the `layout` prop array for <GridLayout>.
 * @param {object[]} cards — each card must have { id, cardType, position: { x, y, w, h } }
 */
export function toRGLLayout(cards) {
  if (!cards || !Array.isArray(cards)) return [];
  return cards
    .filter(Boolean)
    .map((card) => {
      const defs = getDefaults(card.cardType);
      const pos = card.position || {};
      return {
        i:    String(card.id),
        x:    typeof pos.x === 'number' ? pos.x : 0,
        y:    typeof pos.y === 'number' ? pos.y : 0,
        w:    typeof pos.w === 'number' ? pos.w : defs.w,
        h:    typeof pos.h === 'number' ? pos.h : defs.h,
        minW: defs.minW,
        minH: defs.minH,
      };
    });
}

/**
 * Convert the onLayoutChange array back to the positions payload for updateCardPositions.
 */
export function fromRGLLayout(rglLayout) {
  if (!rglLayout || !Array.isArray(rglLayout)) return [];
  return rglLayout.map(({ i, x, y, w, h }) => ({
    id: i,
    position: { x, y, w, h },
  }));
}

/**
 * Find the first free grid position for a new card of the given type.
 * Scans top-to-bottom, left-to-right. Falls back to below all existing cards.
 */
export function defaultGridPosition(cardType, existingLayout) {
  const defs = getDefaults(cardType);
  const { w: defW, h: defH } = defs;
  const layout = existingLayout || [];

  const occupied = new Set();
  layout.forEach((item) => {
    for (let dy = 0; dy < item.h; dy++)
      for (let dx = 0; dx < item.w; dx++)
        occupied.add(`${item.x + dx},${item.y + dy}`);
  });

  const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  for (let y = 0; y <= maxY + 1; y++) {
    for (let x = 0; x <= GRID_COLS - defW; x++) {
      let free = true;
      outer: for (let dy = 0; dy < defH; dy++)
        for (let dx = 0; dx < defW; dx++)
          if (occupied.has(`${x + dx},${y + dy}`)) { free = false; break outer; }
      if (free) return { x, y, w: defW, h: defH };
    }
  }

  return { x: 0, y: maxY, w: defW, h: defH };
}

/**
 * Returns true when position is already in the { x, y, w, h } grid format.
 * Kept as a small helper for cards that may have a null/undefined position
 * (e.g. defaultGridPosition fallback callers).
 */
export function isGridPosition(pos) {
  return pos != null && typeof pos.x === 'number' && typeof pos.y === 'number';
}
